// Keep return navigation in this browser tab, never in the shared Rails session.
// Import before Turbo: Turbo replaces history.state when it starts after a reload.
const KEY = "echoageAdminNavigation"
const STORAGE = "echoage-admin-navigation-v1"
const LIMIT = 20

function adminURL(value) {
  if (!value) return null
  try {
    const url = new URL(value, location.origin)
    if (url.origin !== location.origin || url.username || url.password || !/^\/admin(?:\/|$)/.test(url.pathname)) return null
    if (/\.(?:json|csv|pdf|xlsx|zip|js)$|^\/admin\/pwa(?:\/|$)/i.test(url.pathname)) return null
    return `${url.pathname}${url.search}${url.hash}`
  } catch { return null }
}

// Filters, pagination, editing and profile tabs belong to the same page.
// Individual reports and the parent/party directories are distinct destinations.
function pageKey(value) {
  const url = new URL(value, location.origin)
  const section = { "/admin/reports": "report", "/admin/parents-parties": "mode" }[url.pathname]
  return url.pathname + (section ? `?${section}=${url.searchParams.get(section) || (section === "mode" ? "parents" : "")}` : "")
}

function cleanPage(page) {
  const url = adminURL(page?.url)
  return url ? { url, label: typeof page.label === "string" ? page.label.slice(0, 160) : "" } : null
}

function cleanState(value) {
  const page = cleanPage(value?.page)
  return page ? { page, trail: Array.isArray(value.trail) ? value.trail.slice(-LIMIT).map(cleanPage).filter(Boolean) : [] } : null
}

function install() {
  if (window.__echoageAdminNavigation) return
  window.__echoageAdminNavigation = true
  let initial = cleanState(history.state?.[KEY])
  let current, restoring, pending, firstLoad = true
  let saved = {}
  try { saved = JSON.parse(sessionStorage.getItem(STORAGE)) || {} } catch { /* Storage is optional. */ }
  let recent = Array.isArray(saved.recent) ? saved.recent.map(cleanState).filter(Boolean).slice(-LIMIT) : []
  pending = saved.pending

  const active = () => document.body?.hasAttribute("data-admin-navigation-label") && adminURL(location.href)
  const pageLabel = () => document.querySelector("[data-admin-navigation-page-label]")?.dataset.adminNavigationPageLabel ||
    document.body.dataset.adminNavigationLabel || "Admin"
  const persist = () => {
    try { sessionStorage.setItem(STORAGE, JSON.stringify({ recent, pending })) } catch { /* Private browsing/quota. */ }
  }
  const remember = () => {
    recent = recent.filter(item => item.page.url !== current.page.url).concat(current).slice(-LIMIT)
    persist()
    // Preserve Turbo's own restoration identifiers and any other history metadata.
    history.replaceState({ ...history.state, [KEY]: current }, "")
  }
  const snapshot = () => {
    if (!active() || !current || pageKey(current.page.url) !== pageKey(location.href)) return null
    current = { ...current, page: { url: adminURL(location.href), label: pageLabel() } }
    remember()
    return current
  }
  const render = () => {
    const previous = current?.trail.at(-1)
    document.querySelectorAll("[data-admin-back]").forEach(link => {
      const url = previous?.url || link.dataset.adminBackFallback
      const label = previous ? (previous.label ? `Back to ${previous.label}` : "Back") : link.dataset.adminBackFallbackLabel
      link.setAttribute("href", url)
      link.querySelector("[data-admin-back-label]").textContent = label
    })
  }
  const arriveFrom = (source, url, back = false) => {
    if (back) {
      const index = source.trail.findLastIndex(page => page.url === url)
      // Returning must unwind the trail, not create a loop back to the detail page.
      return { trail: index >= 0 ? source.trail.slice(0, index) : [] }
    }
    return { trail: pageKey(source.page.url) === pageKey(url) ? source.trail : [...source.trail, source.page].slice(-LIMIT) }
  }
  const load = () => {
    if (document.documentElement.hasAttribute("data-turbo-preview")) return
    const url = active()
    if (!url) {
      current = pending = initial = restoring = null
      recent = []
      persist()
      firstLoad = false
      return
    }
    let state = restoring || (firstLoad ? initial : cleanState(history.state?.[KEY]))
    if (!state || pageKey(state.page.url) !== pageKey(url)) state = null
    const source = cleanState(pending?.source)
    if (source && adminURL(pending.url) === url && Date.now() - pending.at < 60_000) {
      state = arriveFrom(source, url, pending.back)
    }
    if (!state && current) state = arriveFrom(current, url)
    if (!state && firstLoad) {
      const navigationType = performance.getEntriesByType("navigation")[0]?.type
      if (["reload", "back_forward"].includes(navigationType)) {
        state = recent.findLast(item => item.page.url === url)
      }
      if (!state) {
        const referrer = adminURL(document.referrer)
        if (referrer) {
          const origin = recent.findLast(item => item.page.url === referrer) || { page: { url: referrer, label: "" }, trail: [] }
          state = arriveFrom(origin, url)
        }
      }
    }
    current = { page: { url, label: pageLabel() }, trail: state?.trail || [] }
    pending = initial = restoring = null
    firstLoad = false
    remember()
    render()
  }
  const prepare = (value, back = false) => {
    const url = adminURL(value)
    const source = snapshot()
    if (!url || !source) return
    // A native click is followed by turbo:before-visit; keep its back-link intent.
    back ||= pending?.url === url && pending?.source?.page.url === source.page.url && pending.back
    pending = { url, source, back: !!back, at: Date.now() }
    persist()
  }

  document.addEventListener("click", event => {
    if (!active()) return
    snapshot() // Also captures the exact source for links opened in another tab.
    const link = event.target.closest?.("a[href]")
    if (!link || event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey ||
        link.hasAttribute("download") || link.hasAttribute("data-turbo-method") || link.hasAttribute("data-method") ||
        (link.target && !["_self", "_top"].includes(link.target))) return
    const frame = link.dataset.turboFrame || link.closest("turbo-frame")?.id
    if (frame && frame !== "_top") return
    prepare(link.href, link.hasAttribute("data-admin-back"))
  }, true)
  document.addEventListener("turbo:before-visit", event => prepare(event.detail.url))
  document.addEventListener("turbo:load", load)
  document.addEventListener("turbo:frame-load", event => {
    if (!active()) return
    // Some directory pagers update only a frame. Keep their displayed page and
    // filters in the URL too, so a return link can reproduce that exact view.
    // Drawer/API/lazy-report endpoints have different page keys and are ignored.
    const url = adminURL(event.target.src)
    if (url && pageKey(url) === pageKey(location.href)) history.replaceState(history.state, "", url)
    // Marketing sections can promote a frame visit to a different page URL.
    if (current && pageKey(current.page.url) !== pageKey(location.href)) return load()
    snapshot()
    render()
  })
  window.addEventListener("pagehide", snapshot)
  window.addEventListener("pageshow", event => { if (event.persisted) load() })
  window.addEventListener("popstate", event => {
    restoring = cleanState(event.state?.[KEY])
    pending = null
    persist()
  })
  if (document.readyState === "loading") document.addEventListener("DOMContentLoaded", load, { once: true })
  else load()
}

install()
